import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import InfiniteScroll from '../components/InfiniteScroll';
import Silk from '../components/Silk';
import { TrendingUp, TrendingDown, BarChart3, Zap, Eye, Activity, ArrowRight, Wifi, WifiOff } from 'lucide-react';
import magmaLogo from '../assets/logos/LOGO RR MAGMA.svg';
import '../App.css';

const API_URL = '/api/go';
const WS_URL  = 'wss://100.119.212.17:8080/ws';

// PNG loaded via require() — sidesteps whatever broke the ES-module import last time
const magmaBanner = require('../assets/logos/magma_banner_deitado.png');

// ─── Design tokens (identidade nova) ──────────────────────────────────────
const C = {
  bg:          '#0e0d1f',
  card:        'rgba(18,18,18,0.8)',
  brand:       '#6660fe',
  brandSoft:   'rgba(102,96,254,0.14)',
  brandLine:   'rgba(102,96,254,0.3)',
  gold:        '#D4AF37',
  green:       '#22c55e',
  red:         '#DC143C',
  blue:        '#3b82f6',
  text:        '#fff',
  textSec:     'rgba(255,255,255,0.7)',
  textMut:     'rgba(255,255,255,0.4)',
  textFaint:   'rgba(255,255,255,0.22)',
  border:      'rgba(255,255,255,0.06)',
  borderStrong:'rgba(255,255,255,0.12)',
};
const F = {
  header:  "'Roble',system-ui,sans-serif",
  body:    "'Plus Jakarta Sans',system-ui,sans-serif",
  mono:    "'IBM Plex Mono',monospace",
};

const fmtNum = (n, d = 2) =>
  n == null || isNaN(n) ? '—'
  : n.toLocaleString('pt-BR', { minimumFractionDigits: d, maximumFractionDigits: d });

const fmtVol = (v) => {
  if (!v) return '—';
  if (v >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return String(v);
};

// ─── FALLBACK DATA (shown until API responds) ─────────────────────────────
const FALLBACK_GAINERS = [
  { ticker: 'CPLE6', ult: 11.82, var: 4.21, name: 'Copel' },
  { ticker: 'ELET3', ult: 39.14, var: 3.87, name: 'Eletrobrás' },
  { ticker: 'SUZB3', ult: 58.40, var: 2.94, name: 'Suzano' },
  { ticker: 'WEGE3', ult: 47.22, var: 2.41, name: 'WEG' },
  { ticker: 'ITUB4', ult: 33.80, var: 1.98, name: 'Itaú' },
  { ticker: 'BBAS3', ult: 26.72, var: 1.74, name: 'Banco do Brasil' },
  { ticker: 'RENT3', ult: 53.10, var: 1.42, name: 'Localiza' },
  { ticker: 'RDOR3', ult: 30.64, var: 1.21, name: 'Rede D\'Or' },
];

const FALLBACK_LOSERS = [
  { ticker: 'MGLU3', ult:  1.84, var: -5.13, name: 'Magazine Luiza' },
  { ticker: 'VIIA3', ult:  0.72, var: -4.42, name: 'Grupo Casas Bahia' },
  { ticker: 'BRAP4', ult: 24.10, var: -2.84, name: 'Bradespar' },
  { ticker: 'VALE3', ult: 61.14, var: -0.87, name: 'Vale' },
  { ticker: 'CSNA3', ult:  9.28, var: -2.21, name: 'Siderúrgica Nacional' },
  { ticker: 'CSAN3', ult: 18.44, var: -1.88, name: 'Cosan' },
  { ticker: 'PRIO3', ult: 44.80, var: -1.62, name: 'PRIO' },
  { ticker: 'AZUL4', ult:  5.42, var: -1.44, name: 'Azul' },
];

const FALLBACK_ACTIVE = [
  { ticker: 'PETR4', ult: 38.42, var: 2.3,  vol: 87400000, name: 'Petrobras PN' },
  { ticker: 'VALE3', ult: 61.14, var: -0.87, vol: 62100000, name: 'Vale ON' },
  { ticker: 'ITUB4', ult: 33.80, var: 1.98,  vol: 44800000, name: 'Itaú PN' },
  { ticker: 'BBDC4', ult: 13.45, var: 1.5,   vol: 38200000, name: 'Bradesco PN' },
  { ticker: 'ABEV3', ult: 12.34, var: -0.3,  vol: 32600000, name: 'Ambev ON' },
  { ticker: 'B3SA3', ult: 11.23, var: 3.2,   vol: 29400000, name: 'B3 ON' },
];

// ─── MACRO STATS (paleta nova) ─────────────────────────────────────────────
const MACRO_STATS = [
  { label: 'SELIC',      value: '14,75%', sub: 'Meta vigente',    color: C.brand },
  { label: 'IPCA 12M',   value: '5,49%',  sub: 'IBGE · mar/25',   color: C.gold },
  { label: 'USD/BRL',    value: '5,74',   sub: 'Câmbio comercial', color: '#f97316' },
  { label: 'IBOVESPA',   value: '130.8K', sub: 'Pontos',          color: C.green },
];

// ─── PRODUCT PILLARS (paleta batendo com badges do dashboard) ─────────────
const PILLARS = [
  {
    icon: <Eye size={18} />,
    name: 'RR Surveillance',
    tag: 'Narrative Radar',
    color: C.gold,
    desc: 'Banco de hipóteses sob vigilância ativa. Narrativas agrupadas por sobreposição de componentes — veja onde convergem ou competem.',
    href: '/thesis',
  },
  {
    icon: <Activity size={18} />,
    name: 'RR Genoma',
    tag: 'Causal Signals',
    color: '#9333ea',
    desc: 'Sinais causais via cadeias de suprimento. Correlações estruturais entre fluxos Siscomex, ordens de produção e índices de preço.',
    href: '/genoma',
  },
  {
    icon: <Zap size={18} />,
    name: 'RR TV',
    tag: 'Broadcast Ao Vivo',
    color: C.brand,
    desc: 'Pipeline de broadcast financeiro totalmente automatizado — TTS, síntese de voz e análise editorial em tempo real.',
    href: '/tv',
  },
  {
    icon: <BarChart3 size={18} />,
    name: 'DINO',
    tag: 'Media Intelligence',
    color: C.green,
    desc: '4M+ artigos · 26 anos · G20. Motor multilíngue de inteligência de mídia com cobertura jornalística profunda e análise de sentimento.',
    href: '/dino',
  },
];

// ═══════════════════════════════════════════════════════════════════════════
// SECTION KICKER (padrão de section header do dashboard, adaptado)
// ═══════════════════════════════════════════════════════════════════════════
function SectionKicker({ label, title, accent = C.brand }) {
  return (
    <div style={{ position: 'relative', marginBottom: '3rem', paddingLeft: 18 }}>
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: `linear-gradient(180deg,${accent},rgba(102,96,254,0))`, borderRadius: 2 }} />
      <div style={{ fontFamily: F.mono, fontSize: 10, fontWeight: 700, letterSpacing: '0.28em', color: 'rgba(102,96,254,0.6)', marginBottom: 8, textTransform: 'uppercase' }}>{label}</div>
      <h2 style={{ fontFamily: F.header, fontSize: 'clamp(1.6rem, 2.4vw, 2rem)', fontWeight: 800, color: C.text, margin: 0, letterSpacing: '-0.02em', lineHeight: 1 }}>{title}</h2>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
function HomePage() {
  const [marketData, setMarketData]   = useState(null);
  const [isLive,     setIsLive]       = useState(false);
  const [lastUpdate, setLastUpdate]   = useState(null);
  const [intensity,  setIntensity]    = useState(74);
  const [userCount,  setUserCount]    = useState(3847);
  const tickerRef = useRef(null);

  const gainers = marketData?.topGainers  ?? FALLBACK_GAINERS;
  const losers  = marketData?.topLosers   ?? FALLBACK_LOSERS;
  const active  = marketData?.mostActive  ?? FALLBACK_ACTIVE;
  const wkG     = marketData?.topWeeklyGainers ?? [];
  const wkL     = marketData?.topWeeklyLosers  ?? [];

  const fetchData = async () => {
    try {
      const r = await fetch(`${API_URL}/api/overview`);
      const d = await r.json();
      setMarketData(d);
      setLastUpdate(new Date(d.lastUpdated));
    } catch {}
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    const ws = new WebSocket(WS_URL);
    ws.onopen    = () => setIsLive(true);
    ws.onclose   = () => setIsLive(false);
    ws.onerror   = () => setIsLive(false);
    ws.onmessage = (e) => {
      try {
        const d = JSON.parse(e.data);
        setMarketData(d);
        setLastUpdate(new Date(d.lastUpdated));
      } catch {}
    };
    return () => ws.close();
  }, []);

  useEffect(() => {
    const i1 = setInterval(() => setIntensity(p => Math.max(60, Math.min(94, p + (Math.random() - 0.48) * 3.2))), 1700);
    const i2 = setInterval(() => setUserCount(p => p + Math.floor((Math.random() - 0.3) * 7)), 2400);
    return () => { clearInterval(i1); clearInterval(i2); };
  }, []);

  // ── InfiniteScroll item builders ──
  const buildScrollItem = (t, changeColor) => ({
    content: (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '0 4px' }}>
        <div>
          <div style={{ fontFamily: F.mono, fontWeight: 700, fontSize: 13, color: C.text, letterSpacing: '0.04em' }}>{t.ticker}</div>
          <div style={{ fontFamily: F.body, fontWeight: 400, fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>{t.name || fmtVol(t.vol)}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontFamily: F.body, fontWeight: 700, fontSize: 13, color: changeColor(t.var) }}>
            {t.var >= 0 ? '+' : ''}{t.var?.toFixed(2)}%
          </div>
          <div style={{ fontFamily: F.body, fontWeight: 600, fontSize: 11, color: C.textMut, marginTop: 2 }}>R$ {fmtNum(t.ult)}</div>
        </div>
      </div>
    )
  });

  const scrollGainers = gainers.slice(0, 10).map(t => buildScrollItem(t, () => C.green));
  const scrollLosers  = losers.slice(0, 10).map(t => buildScrollItem(t, () => C.red));
  const scrollActive  = active.slice(0, 10).map(t => buildScrollItem(t, v => v >= 0 ? C.green : C.red));

  const tickerItems = [...gainers.slice(0,6), ...losers.slice(0,6), ...active.slice(0,4)];

  return (
    <div style={{ background: C.bg, minHeight: '100vh', overflowX: 'hidden', fontFamily: F.body, color: C.text }}>

      {/* ══ SILK BACKGROUND (identidade nova) ══════════════════════════════ */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 0, opacity: 0.22, pointerEvents: 'none' }}>
        <Silk speed={31} scale={1.5} color={C.brand} noiseIntensity={2} rotation={0.2} />
      </div>

      {/* ══ KEYFRAMES + MEDIA QUERIES ══════════════════════════════════════ */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;600;700;800&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        *, *::before, *::after { box-sizing: border-box; }
        @keyframes bgScrollCol0  { from{transform:translateY(0)} to{transform:translateY(-50%)} }
        @keyframes bgScrollCol1  { from{transform:translateY(0)} to{transform:translateY(-50%)} }
        @keyframes bgScrollCol2  { from{transform:translateY(0)} to{transform:translateY(-50%)} }
        @keyframes scrollTicker  { from{transform:translateX(0)} to{transform:translateX(-50%)} }
        @keyframes pulseGlow     { 0%,100%{opacity:1} 50%{opacity:0.28} }
        @keyframes fadeUp        { from{opacity:0;transform:translateY(28px)} to{opacity:1;transform:translateY(0)} }
        .rr-cta-primary { transition: all 0.22s ease; }
        .rr-cta-primary:hover { background: linear-gradient(90deg,#6660fe,#8b5cf6) !important; transform: translateY(-2px); box-shadow: 0 8px 32px rgba(102,96,254,0.45) !important; }
        .rr-cta-secondary { transition: all 0.22s ease; }
        .rr-cta-secondary:hover { background: rgba(255,255,255,0.08) !important; border-color: rgba(102,96,254,0.4) !important; transform: translateY(-2px); }
        .rr-pillar-card { transition: all 0.25s ease; cursor: pointer; }
        .rr-pillar-card:hover { transform: translateY(-4px); border-color: rgba(102,96,254,0.3) !important; }
        .rr-chip { transition: all 0.18s ease; }
        .rr-chip:hover { transform: scale(1.04); }

        /* ═══ MOBILE (≤768px) ═══════════════════════════════════════════ */
        @media (max-width: 768px) {
          .rr-hero-section    { min-height: auto !important; padding-bottom: 4.5rem !important; width: 100% !important; max-width: 100vw !important; overflow-x: hidden !important; }
          .rr-hero-grid       { grid-template-columns: 1fr !important; gap: 2rem !important; min-height: auto !important; padding: 5rem 1.25rem 2rem !important; width: 100% !important; max-width: 100% !important; }
          .rr-status-bar      { padding: 1rem 1.25rem !important; }
          .rr-status-time     { display: none !important; }
          .rr-rolling-bg      { display: none !important; }
          .rr-hero-logo       { height: 88px !important; }
          .rr-macro-stats     { grid-template-columns: repeat(2,1fr) !important; }
          .rr-ticker-panels   { display: none !important; }
          .rr-cta-wrap        { flex-direction: column !important; }
          .rr-cta-wrap > a    { width: 100%; justify-content: center; }
          .rr-hero-ticker     { position: relative !important; margin-top: 2rem; }
          .rr-section-pad     { padding-left: 1.25rem !important; padding-right: 1.25rem !important; }
          .rr-section-vpad    { padding-top: 3rem !important; padding-bottom: 3rem !important; }
          .rr-scroll-grid     { grid-template-columns: 1fr !important; gap: 1.5rem !important; }
          .rr-scroll-box      { height: 340px !important; }
          .rr-pillars-grid    { grid-template-columns: 1fr !important; }
          .rr-chips-grid      { grid-template-columns: repeat(auto-fill,minmax(108px,1fr)) !important; }
          .rr-footer-inner    { flex-direction: column !important; text-align: center; gap: 14px !important; }
          .rr-footer-links    { justify-content: center; flex-wrap: wrap; gap: 14px !important; }
          .rr-banner-img      { height: auto !important; aspect-ratio: 1080 / 576 !important; background-size: cover !important; background-position: center center !important; opacity: 0.6 !important; }
        }
      `}</style>

      {/* ══ HERO ═════════════════════════════════════════════════════════ */}
      <section className="rr-hero-section" style={{ position: 'relative', minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', overflow: 'hidden' }}>

        {/* ── MAGMA BANNER (background image, plain CSS — no mask, no blend mode) ── */}
        <div style={{ position: 'absolute', inset: 0, zIndex: 0, overflow: 'hidden', pointerEvents: 'none' }}>
          <div className="rr-banner-img" style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '85%',
            backgroundImage: `url(${magmaBanner})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center bottom',
            backgroundRepeat: 'no-repeat',
            opacity: 0.5,
          }} />
          {/* Fade to top */}
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to top, transparent 0%, rgba(14,13,31,0.5) 55%, rgba(14,13,31,0.95) 100%)',
            pointerEvents: 'none',
          }} />
          {/* Side vignette */}
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(ellipse 100% 80% at 50% 100%, transparent 20%, rgba(14,13,31,0.6) 100%)',
            pointerEvents: 'none',
          }} />
        </div>

        {/* Rolling-columns background (hidden on mobile) */}
        <div className="rr-rolling-bg" style={{ position: 'absolute', inset: 0, display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', overflow: 'hidden', opacity: 0.35 }}>
          {[
            { items: gainers.slice(0,8) },
            { items: losers.slice(0,8) },
            { items: active.slice(0,8) },
            { items: [...wkG.slice(0,4), ...wkL.slice(0,4)] },
            { items: gainers.slice(4,12) },
          ].map((col, ci) => {
            const tripled = [...col.items, ...col.items, ...col.items];
            return (
              <div key={ci} style={{ overflow: 'hidden', borderRight: '1px solid rgba(255,255,255,0.025)', fontFamily: F.mono }}>
                <div style={{ animation: `bgScrollCol${Math.min(ci, 2)} ${14 + ci * 2.5}s linear infinite` }}>
                  {tripled.map((t, ri) => (
                    <div key={ri} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid rgba(255,255,255,0.02)' }}>
                      <span style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.18)', letterSpacing: '0.04em' }}>{t.ticker}</span>
                      <span style={{ fontFamily: F.body, fontSize: 9, fontWeight: 700, color: t.var >= 0 ? 'rgba(34,197,94,0.3)' : 'rgba(220,20,60,0.3)' }}>
                        {t.var >= 0 ? '+' : ''}{t.var?.toFixed(2)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Gradient overlay */}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(110deg,rgba(14,13,31,0.97) 0%,rgba(14,13,31,0.88) 42%,rgba(14,13,31,0.55) 68%,rgba(14,13,31,0.2) 100%)', pointerEvents: 'none' }} />

        {/* Purple glow substituindo o vulcão */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '55%', height: '65%', background: 'radial-gradient(ellipse 80% 70% at 5% 100%,rgba(102,96,254,0.35) 0%,transparent 65%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: '20%', right: '-10%', width: '55%', height: '80%', background: 'radial-gradient(ellipse 70% 60% at 100% 40%,rgba(139,92,246,0.18) 0%,transparent 70%)', pointerEvents: 'none' }} />

        {/* ── STATUS BAR ── */}
        <div className="rr-status-bar" style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.2rem 2.5rem', background: 'linear-gradient(to bottom,rgba(14,13,31,0.92),transparent)' }}>
          <img src={magmaLogo} alt="RR Magma" style={{ height: 22, width: 'auto', opacity: 0.9 }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontFamily: F.mono, fontSize: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: isLive ? C.green : 'rgba(255,255,255,0.3)' }}>
              {isLive ? <Wifi size={11} /> : <WifiOff size={11} />}
              <span style={{ fontWeight: 700, letterSpacing: '0.12em' }}>{isLive ? 'LIVE' : 'OFFLINE'}</span>
            </div>
            {lastUpdate && (
              <span className="rr-status-time" style={{ color: C.textFaint, fontFamily: F.body, fontWeight: 600 }}>
                {lastUpdate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </span>
            )}
            <Link to="https://relatorioreservado.com.br/planos" style={{ textDecoration: 'none', fontFamily: F.mono, fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', color: C.brand, border: `1px solid ${C.brandLine}`, padding: '4px 10px', borderRadius: 3 }}>ASSINAR</Link>
          </div>
        </div>

        {/* ── HERO CONTENT ── */}
        <div className="rr-hero-grid" style={{ position: 'relative', zIndex: 2, maxWidth: 1400, margin: '0 auto', padding: '0 2.5rem', display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: '3rem', alignItems: 'center', minHeight: '80vh', width: '100%' }}>

          {/* LEFT: branding + CTA */}
          <div style={{ animation: 'fadeUp 0.7s ease both' }}>
            <div style={{ fontFamily: F.mono, fontSize: 9, fontWeight: 700, letterSpacing: '0.3em', color: C.textFaint, marginBottom: '1.5rem', textTransform: 'uppercase' }}>
              Relatório Reservado · Plataforma de Dados
            </div>

            {/* SVG logo em destaque (substitui wordmark gradiente) */}
            <img className="rr-hero-logo" src={magmaLogo} alt="RR Magma" style={{ display: 'block', height: 'clamp(88px, 11vw, 128px)', width: 'auto', marginBottom: '1.5rem', filter: 'drop-shadow(0 8px 32px rgba(102,96,254,0.35))' }} />

            <p style={{ fontFamily: F.header, fontSize: 'clamp(20px, 2vw, 26px)', fontWeight: 800, color: C.text, margin: '0 0 0.6rem', lineHeight: 1.15, letterSpacing: '-0.02em' }}>
              Profundidade de mercado.<br />
              <span style={{ color: 'rgba(255,255,255,0.55)' }}>Em tempo real. Sem atenuação.</span>
            </p>
            <p style={{ fontFamily: F.body, fontSize: 13, fontWeight: 400, lineHeight: 1.7, color: 'rgba(255,255,255,0.4)', margin: '0 0 2rem', maxWidth: 460 }}>
              Order book L2 · COPOM · Índices globais · 60k+ artigos RR · Cobertura G20
            </p>

            {/* Intensity bar */}
            <div style={{ marginBottom: '1.8rem', maxWidth: 460 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, fontFamily: F.mono, fontSize: 9, letterSpacing: '0.18em' }}>
                <span style={{ color: C.textFaint, fontWeight: 700 }}>INTENSIDADE DO MERCADO</span>
                <span style={{ color: C.brand, fontWeight: 800 }}>{Math.round(intensity)}%</span>
              </div>
              <div style={{ height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.07)', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${intensity}%`, background: 'linear-gradient(90deg,#312e81,#4f46e5,#6660fe,#818cf8,#a5b4fc)', borderRadius: 2, transition: 'width 0.7s ease' }} />
              </div>
            </div>

            {/* CTAs */}
            <div className="rr-cta-wrap" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: '2rem' }}>
              <Link to="https://relatorioreservado.com.br/planos" className="rr-cta-primary" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 8, background: C.brand, color: '#fff', fontFamily: F.mono, fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', padding: '13px 24px', borderRadius: 4, border: 'none', cursor: 'pointer', boxShadow: '0 4px 20px rgba(102,96,254,0.35)' }}>
                <Zap size={14} /> ASSINAR RR MAGMA
              </Link>
              <Link to="/mercado" className="rr-cta-secondary" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.04)', color: C.textSec, fontFamily: F.mono, fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', padding: '13px 24px', borderRadius: 4, border: `1px solid ${C.borderStrong}`, cursor: 'pointer' }}>
                <BarChart3 size={14} /> VER DASHBOARD
              </Link>
            </div>

            {/* Avatar cluster + user count */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ display: 'flex' }}>
                {[C.brand, '#8b5cf6', C.gold, C.green, C.blue].map((c, i) => (
                  <div key={i} style={{ width: 24, height: 24, borderRadius: '50%', background: c, border: `2px solid ${C.bg}`, marginLeft: i > 0 ? -8 : 0, fontSize: 8, fontFamily: F.body, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800 }}>
                    {['A','B','C','D','E'][i]}
                  </div>
                ))}
              </div>
              <span style={{ fontFamily: F.body, fontSize: 12, color: C.textMut, fontWeight: 400 }}>
                <span style={{ fontFamily: F.body, color: C.brand, fontWeight: 700 }}>{userCount.toLocaleString('pt-BR')}</span> analistas conectados
              </span>
            </div>
          </div>

          {/* RIGHT: live ticker panels */}
          <div style={{ animation: 'fadeUp 0.9s 0.15s ease both', opacity: 0, animationFillMode: 'forwards' }}>

            {/* Macro stats row */}
            <div className="rr-macro-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 6, marginBottom: 10 }}>
              {MACRO_STATS.map(m => (
                <div key={m.label} style={{ background: C.card, border: `1px solid ${C.border}`, borderTop: `2px solid ${m.color}`, borderRadius: 6, padding: '10px 12px', backdropFilter: 'blur(8px)' }}>
                  <div style={{ fontFamily: F.mono, fontSize: 8, fontWeight: 700, letterSpacing: '0.14em', color: C.textFaint, marginBottom: 5 }}>{m.label}</div>
                  <div style={{ fontFamily: F.body, fontSize: 18, fontWeight: 700, color: m.color, lineHeight: 1 }}>{m.value}</div>
                  <div style={{ fontFamily: F.body, fontSize: 9, fontWeight: 400, color: 'rgba(255,255,255,0.35)', marginTop: 4 }}>{m.sub}</div>
                </div>
              ))}
            </div>

            {/* Three ticker columns — hidden on mobile */}
            <div className="rr-ticker-panels" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, height: 340 }}>
              {[
                { label: 'ALTAS · B3',    color: C.green,   items: gainers.slice(0,10), anim: 'bgScrollCol0', dur: 12 },
                { label: 'BAIXAS · B3',   color: C.red,     items: losers.slice(0,10),  anim: 'bgScrollCol1', dur: 15 },
                { label: 'VOLUME LÍDER',  color: C.brand,   items: active.slice(0,10),  anim: 'bgScrollCol2', dur: 18 },
              ].map((col, ci) => {
                const tripled = [...col.items, ...col.items, ...col.items];
                return (
                  <div key={ci} style={{ background: C.card, border: `1px solid ${C.border}`, borderTop: `2px solid ${col.color}`, borderRadius: 8, overflow: 'hidden', backdropFilter: 'blur(10px)' }}>
                    <div style={{ padding: '8px 10px 7px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontFamily: F.mono, fontSize: 8, fontWeight: 700, letterSpacing: '0.14em', color: col.color }}>{col.label}</span>
                      <span style={{ width: 5, height: 5, borderRadius: '50%', background: col.color, boxShadow: `0 0 6px ${col.color}`, animation: 'pulseGlow 1.5s infinite', display: 'inline-block' }} />
                    </div>
                    <div style={{ height: 'calc(100% - 32px)', overflow: 'hidden', position: 'relative' }}>
                      <div style={{ animation: `${col.anim} ${col.dur}s linear infinite` }}>
                        {tripled.map((t, ri) => (
                          <div key={ri} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                            <span style={{ fontFamily: F.mono, fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.75)', letterSpacing: '0.03em' }}>{t.ticker}</span>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontFamily: F.body, fontSize: 9, fontWeight: 600, color: 'rgba(255,255,255,0.4)' }}>R$ {fmtNum(t.ult)}</div>
                              <div style={{ fontFamily: F.body, fontSize: 9, fontWeight: 700, color: t.var >= 0 ? C.green : C.red }}>
                                {t.var >= 0 ? '+' : ''}{t.var?.toFixed(2)}%
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 48, background: 'linear-gradient(to top,rgba(18,18,18,0.95),transparent)', pointerEvents: 'none' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── HORIZONTAL TICKER ── */}
        <div className="rr-hero-ticker" style={{ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 10, height: 38, background: 'rgba(14,13,31,0.92)', borderTop: `1px solid ${C.brandLine}`, overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', height: '100%', borderRight: `1px solid ${C.brandLine}`, padding: '0 14px', flexShrink: 0 }}>
            <span style={{ fontFamily: F.mono, fontSize: 8, fontWeight: 800, letterSpacing: '0.18em', color: C.brand, animation: 'pulseGlow 1.5s infinite' }}>● LIVE</span>
          </div>
          <div style={{ overflow: 'hidden', flex: 1 }}>
            <div ref={tickerRef} style={{ display: 'flex', animation: 'scrollTicker 40s linear infinite', whiteSpace: 'nowrap' }}>
              {[0,1].map(dup => (
                <span key={dup} style={{ display: 'inline-flex', gap: 0 }}>
                  {tickerItems.map((t, i) => (
                    <span key={`${dup}-${i}`} style={{ fontSize: 10, color: C.textSec, padding: '0 20px', display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                      <b style={{ fontFamily: F.mono, color: C.text, fontWeight: 700, letterSpacing: '0.03em' }}>{t.ticker}</b>
                      <span style={{ fontFamily: F.body, fontWeight: 600, color: 'rgba(255,255,255,0.35)' }}>R$ {fmtNum(t.ult)}</span>
                      <span style={{ fontFamily: F.body, color: t.var >= 0 ? C.green : C.red, fontWeight: 700 }}>
                        {t.var >= 0 ? '▲ +' : '▼ '}{t.var?.toFixed(2)}%
                      </span>
                    </span>
                  ))}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ LIVE MARKET CHIPS ════════════════════════════════════════════ */}
      <section className="rr-section-vpad" style={{ position: 'relative', zIndex: 1, background: 'rgba(14,13,31,0.85)', borderBottom: `1px solid ${C.border}`, padding: '2.5rem 0', backdropFilter: 'blur(8px)' }}>
        <div className="rr-section-pad" style={{ maxWidth: 1400, margin: '0 auto', padding: '0 2.5rem' }}>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <TrendingUp size={14} color={C.green} />
              <span style={{ fontFamily: F.mono, fontSize: 9, fontWeight: 700, letterSpacing: '0.22em', color: C.textMut }}>MERCADO AGORA</span>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: C.green, animation: 'pulseGlow 1.5s infinite', display: 'inline-block' }} />
            </div>
            <Link to="/mercado" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: F.body, fontSize: 12, fontWeight: 500, color: C.textMut, borderBottom: '1px solid rgba(255,255,255,0.15)', paddingBottom: 1 }}>
              Ver todos <ArrowRight size={11} />
            </Link>
          </div>

          <div className="rr-chips-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))', gap: 6 }}>
            {[...gainers.slice(0,6), ...losers.slice(0,6)].map(t => {
              const up = t.var >= 0;
              return (
                <div key={t.ticker} className="rr-chip" style={{ background: up ? 'rgba(34,197,94,0.06)' : 'rgba(220,20,60,0.06)', border: `1px solid ${up ? 'rgba(34,197,94,0.18)' : 'rgba(220,20,60,0.18)'}`, borderRadius: 6, padding: '10px 12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                    <span style={{ fontFamily: F.mono, fontSize: 12, fontWeight: 700, color: C.text, letterSpacing: '0.04em' }}>{t.ticker}</span>
                    <span style={{ fontFamily: F.body, fontSize: 11, fontWeight: 700, color: up ? C.green : C.red }}>
                      {up ? '+' : ''}{t.var?.toFixed(2)}%
                    </span>
                  </div>
                  <div style={{ fontFamily: F.body, fontSize: 10, fontWeight: 500, color: 'rgba(255,255,255,0.4)' }}>R$ {fmtNum(t.ult)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══ INFINITE SCROLL SHOWCASE ═════════════════════════════════════ */}
      <section className="rr-section-vpad" style={{ position: 'relative', zIndex: 1, padding: '5rem 0', background: C.bg }}>
        <div className="rr-section-pad" style={{ maxWidth: 1400, margin: '0 auto', padding: '0 2.5rem' }}>

          <SectionKicker label="Fluxo B3 · Tempo Real" title="Pulso do Mercado" />

          <div className="rr-scroll-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '3rem', alignItems: 'start' }}>

            {/* ALTAS */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1rem' }}>
                <TrendingUp size={13} color={C.green} />
                <span style={{ fontFamily: F.mono, fontSize: 9, fontWeight: 700, letterSpacing: '0.18em', color: C.green }}>MAIORES ALTAS</span>
              </div>
              <div className="rr-scroll-box" style={{ height: 460, position: 'relative', background: 'rgba(34,197,94,0.03)', border: '1px solid rgba(34,197,94,0.1)', borderRadius: 10, overflow: 'hidden', padding: '12px' }}>
                <InfiniteScroll
                  items={scrollGainers.length > 0 ? scrollGainers : [{ content: <div style={{color: C.textMut, fontFamily: F.mono, fontSize:11}}>Carregando…</div> }]}
                  width="100%"
                  isTilted={false}
                  autoplay={true}
                  autoplaySpeed={1.2}
                  autoplayDirection="up"
                  pauseOnHover={true}
                  itemMinHeight={72}
                  negativeMargin="-0.8em"
                />
              </div>
            </div>

            {/* BAIXAS */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1rem' }}>
                <TrendingDown size={13} color={C.red} />
                <span style={{ fontFamily: F.mono, fontSize: 9, fontWeight: 700, letterSpacing: '0.18em', color: C.red }}>MAIORES BAIXAS</span>
              </div>
              <div className="rr-scroll-box" style={{ height: 460, position: 'relative', background: 'rgba(220,20,60,0.03)', border: '1px solid rgba(220,20,60,0.1)', borderRadius: 10, overflow: 'hidden', padding: '12px' }}>
                <InfiniteScroll
                  items={scrollLosers.length > 0 ? scrollLosers : [{ content: <div style={{color: C.textMut, fontFamily: F.mono, fontSize:11}}>Carregando…</div> }]}
                  width="100%"
                  isTilted={false}
                  autoplay={true}
                  autoplaySpeed={1.4}
                  autoplayDirection="down"
                  pauseOnHover={true}
                  itemMinHeight={72}
                  negativeMargin="-0.8em"
                />
              </div>
            </div>

            {/* MAIS ATIVOS */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1rem' }}>
                <Activity size={13} color={C.brand} />
                <span style={{ fontFamily: F.mono, fontSize: 9, fontWeight: 700, letterSpacing: '0.18em', color: C.brand }}>MAIS NEGOCIADOS</span>
              </div>
              <div className="rr-scroll-box" style={{ height: 460, position: 'relative', background: 'rgba(102,96,254,0.04)', border: '1px solid rgba(102,96,254,0.15)', borderRadius: 10, overflow: 'hidden', padding: '12px' }}>
                <InfiniteScroll
                  items={scrollActive.length > 0 ? scrollActive : [{ content: <div style={{color: C.textMut, fontFamily: F.mono, fontSize:11}}>Carregando…</div> }]}
                  width="100%"
                  isTilted={false}
                  autoplay={true}
                  autoplaySpeed={1.6}
                  autoplayDirection="up"
                  pauseOnHover={true}
                  itemMinHeight={72}
                  negativeMargin="-0.8em"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ PRODUCT PILLARS ══════════════════════════════════════════════ */}
      <section className="rr-section-vpad" style={{ position: 'relative', zIndex: 1, background: 'rgba(14,13,31,0.9)', borderTop: `1px solid ${C.border}`, padding: '5rem 0' }}>
        <div className="rr-section-pad" style={{ maxWidth: 1400, margin: '0 auto', padding: '0 2.5rem' }}>

          <SectionKicker label="Ecossistema · RR Magma" title="Inteligência em Camadas" />

          <div className="rr-pillars-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: '1.5rem' }}>
            {PILLARS.map(p => (
              <Link key={p.name} to={p.href} style={{ textDecoration: 'none' }}>
                <div className="rr-pillar-card" style={{ background: C.card, border: `1px solid ${C.border}`, borderTop: `3px solid ${p.color}`, borderRadius: 10, padding: '1.6rem', backdropFilter: 'blur(8px)', height: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1rem' }}>
                    <div style={{ color: p.color }}>{p.icon}</div>
                    <div>
                      <div style={{ fontFamily: F.header, fontSize: 15, fontWeight: 800, letterSpacing: '-0.01em', color: C.text }}>{p.name}</div>
                      <div style={{ fontFamily: F.mono, fontSize: 8, fontWeight: 700, letterSpacing: '0.18em', color: p.color, marginTop: 3, textTransform: 'uppercase' }}>{p.tag}</div>
                    </div>
                  </div>
                  <p style={{ fontFamily: F.body, fontSize: 13, fontWeight: 400, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6, margin: 0 }}>{p.desc}</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: '1.2rem', fontFamily: F.mono, fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', color: p.color }}>
                    EXPLORAR <ArrowRight size={10} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ══ FOOTER ═══════════════════════════════════════════════════════ */}
      <footer style={{ background: 'rgba(14,13,31,0.95)', borderTop: `1px solid ${C.brandLine}`, padding: '2rem 0' }}>
        <div className="rr-footer-inner rr-section-pad" style={{ maxWidth: 1400, margin: '0 auto', padding: '0 2.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <img src={magmaLogo} alt="RR Magma" style={{ height: 26, width: 'auto', opacity: 0.5 }} />
          <p style={{ fontFamily: F.body, fontWeight: 400, color: 'rgba(255,255,255,0.35)', fontSize: '0.85rem', margin: 0 }}>
            © 2025 Relatório Reservado. Dados para fins informativos.
          </p>
          <div className="rr-footer-links" style={{ display: 'flex', gap: 20 }}>
            {[['Entrar', '#'], ['Assinar', '/assinar'], ['Dashboard', '/mercado']].map(([label, href]) => (
              <a key={label} href={href} style={{ fontFamily: F.mono, fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', color: C.textFaint, textDecoration: 'none', textTransform: 'uppercase' }}>{label}</a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}

export default HomePage;